import {inject, Injectable} from "@angular/core";
import {TimeService} from "@services/time-service";
import {Entity, EntityEnum} from '@cureskin/api-client-rn';

@Injectable({ providedIn: 'root' })
export class OrderService {
  private readonly timeService: TimeService = inject(TimeService);

  getLatestDeliveredRegimenOrder(orders: Array<Entity['Order']>): Entity['Order'] | null {
    const { REGIMEN } = EntityEnum.Order.Type;
    const { DELIVERED } = EntityEnum.Order.Stage;
    const deliveredRegimenOrders = (orders ?? []).filter(
      (o: Entity['Order']) => o?.type === REGIMEN && o?.stage === DELIVERED,
    );
    if (!deliveredRegimenOrders.length) return null;

    return deliveredRegimenOrders.sort((a: Entity['Order'], b: Entity['Order']) => {
      const aDate = new Date(a?.deliveredOn).getTime();
      const bDate = new Date(b?.deliveredOn).getTime();
      return bDate - aDate;
    })[0];
  }

  getFirstDeliveredRegimenOrder(orders: Array<Entity['Order']>): Entity['Order'] | null {
    const { REGIMEN } = EntityEnum.Order.Type;
    const { DELIVERED } = EntityEnum.Order.Stage;
    const deliveredRegimenOrders = (orders ?? []).filter((order: Entity['Order']): boolean =>
      order?.type === REGIMEN && order?.stage === DELIVERED && !!order?.deliveredOn);
    if (!deliveredRegimenOrders.length) return null;

    return deliveredRegimenOrders.sort((a: Entity['Order'], b: Entity['Order']): number => {
      const aDate = new Date(a.deliveredOn).getTime();
      const bDate = new Date(b.deliveredOn).getTime();
      return aDate - bDate;
    })[0];
  }

  getDeliveredOn(order: Entity['Order']): Date {
    return new Date(order?.deliveredOn);
  }

  getDaysSinceDelivered(order: Entity['Order']): number {
    return this.timeService.differenceInDays(new Date(), this.getDeliveredOn(order));
  }

  shouldShowPartialOrderCancellationUI(order: Entity['Order']): boolean {
    if(!order) return false;
    if (order.type !== EntityEnum.Order.Type.PRODUCT) return false;
    if (!order.productApprovalDetails?.productDetails?.length) return false;

    const cancelledProducts = this.getCancelledProducts(order.productApprovalDetails)
    if (!cancelledProducts.length) return false;
    return true;
  }

  getCancelledProducts(productApprovalDetails: Entity['Order']['productApprovalDetails']): Entity['Order']['productApprovalDetails']['productDetails'] {
    if (!productApprovalDetails?.productDetails?.length) return [];
    return productApprovalDetails.productDetails.filter((productDetails) => productDetails.status === EntityEnum.Order.ProductApprovalStatus.REJECTED);
  }

  initializeProductApprovalData(order: Entity['Order']): {
    cancelledProducts: Entity['Order']['productApprovalDetails']['productDetails'];
    orderedProductsCount: number;
    shouldShowPartialOrderCancellationUI: boolean;
    cancelledProductsCount: number;
    totalProductsCount: number;
  } {
    if (!order) {
      return {
        cancelledProducts: [],
        orderedProductsCount: 0,
        shouldShowPartialOrderCancellationUI: false,
        cancelledProductsCount: 0,
        totalProductsCount: 0,
      };
    }

    const cancelledProducts = this.getCancelledProducts(order.productApprovalDetails);
    const orderedProductsCount = order.products?.length || 0;
    const shouldShowPartialOrderCancellationUI = this.shouldShowPartialOrderCancellationUI(order);
    const cancelledProductsCount = cancelledProducts.length;
    const totalProductsCount = orderedProductsCount + cancelledProductsCount;

    return {
      cancelledProducts,
      orderedProductsCount,
      shouldShowPartialOrderCancellationUI,
      cancelledProductsCount,
      totalProductsCount,
    };
  }

  shouldHideProductDetails(order: Entity['Order'], cancelledProducts: Entity['Order']['productApprovalDetails']['productDetails']): boolean {
    const cancelledStages: string[] = [
      EntityEnum.Order.Stage.CANCELED,
      EntityEnum.Order.Stage.RETURN_REQUESTED,
      EntityEnum.Order.Stage.REFUND_INITIATED,
      EntityEnum.Order.Stage.REFUND_PROCESSED,
      EntityEnum.Order.Stage.RETURNED,
      EntityEnum.Order.Stage.LOST_IN_TRANSIT,
      EntityEnum.Order.Stage.COURIER_NOT_AVAILABLE,
      EntityEnum.Order.Stage.BOT_CANCELED,
    ]
    if (!cancelledStages.includes(order.stage)) {
      return false;
    }
    if (!cancelledProducts.length) {
      return false;
    }
    if (cancelledProducts.length !== order.productInfo?.length) {
      return false;
    }
    return true;
  }
}
